import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { getCommunicationHistoryGroups, getCommunicationHistoryByGroup } from '../../api/communicationTemplates';

const CommunicationHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [selectedGroup, setSelectedGroup] = useState(null);

  // Template content modal
  const [showContentModal, setShowContentModal] = useState(false);
  const [modalSubject, setModalSubject] = useState('');
  const [modalContent, setModalContent] = useState('');
  
  // Filters
  const [filterType, setFilterType] = useState('all'); // all, email, sms
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, success, failed
  const [isTestFilter, setIsTestFilter] = useState('all'); // all, true, false
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [detailPage, setDetailPage] = useState(1);
  const limit = 10;

  // Company selection logic
  let companyOptions = [];
  const companiesStr = localStorage.getItem('companies');
  if (companiesStr) {
    try {
      const companies = JSON.parse(companiesStr);
      if (Array.isArray(companies) && companies.length > 0) {
        companyOptions = [
          ...companies.map(c => ({
            value: c.id,
            label: c.company_name
          }))
        ];
      }
    } catch (e) {
      companyOptions = [];
    }
  }
  const [selectedCompany, setSelectedCompany] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, [currentPage, detailPage, filterType, search, startDate, endDate, statusFilter, isTestFilter, selectedCompany, selectedGroup]);

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      let response;

      if (selectedGroup) {
        const params = {
          page: detailPage,
          limit,
          group_id: selectedGroup
        };
        if (statusFilter !== 'all') {
          params.status = statusFilter;
        }
        if (startDate) {
          params.start_date = startDate;
        }
        if (endDate) {
          params.end_date = endDate;
        }
        response = await getCommunicationHistoryByGroup(selectedGroup, params);
      } else {
        const params = {
          page: currentPage,
          limit
        };

        if (filterType !== 'all') {
          params.template_type = filterType;
        }

        if (startDate) {
          params.start_date = startDate;
        }

        if (endDate) {
          params.end_date = endDate;
        }

        if (isTestFilter !== 'all') {
          params.is_test = isTestFilter === 'true';
        }

        if (selectedCompany) {
          params.company_id = selectedCompany.value;
        }

        response = await getCommunicationHistoryGroups(params);
      }
      
      if (response.code === 1) {
        setHistory(response.data.data);
        setTotal(response.data.pagination.total);
      } else {
        setError(response.message || 'Failed to fetch communication history');
      }
    } catch (err) {
      console.error('Error fetching communication history:', err);
      setError('Error fetching communication history');
    } finally {
      setLoading(false);
    }
  };

  const handleResetFilters = () => {
    setFilterType('all');
    setSearch('');
    setStartDate('');
    setEndDate('');
    setStatusFilter('all');
    setIsTestFilter('all');
    setSelectedCompany(null);
    setSelectedGroup(null);
    setCurrentPage(1);
    setDetailPage(1);
  };

  const handleViewDetails = (groupId) => {
    setSelectedGroup(groupId);
    setDetailPage(1);
  };

  const handleBackToGroups = () => {
    setSelectedGroup(null);
    setDetailPage(1);
  };

  const handleOpenContentModal = (subject, content) => {
    setModalSubject(subject || 'No Subject');
    setModalContent(content || 'No Content');
    setShowContentModal(true);
  };

  const handleCloseContentModal = () => {
    setShowContentModal(false);
    setModalSubject('');
    setModalContent('');
  };

  const getStatusBadge = (status) => {
    return status === 'success' 
      ? <span className="badge bg-success">Success</span>
      : <span className="badge bg-danger">Failed</span>;
  };

  const getTypeBadge = (type) => {
    return type === 'email'
      ? <span className="badge bg-primary">Email</span>
      : <span className="badge bg-info">SMS</span>;
  };

  const activePage = selectedGroup ? detailPage : currentPage;
  const setActivePage = selectedGroup ? setDetailPage : setCurrentPage;
  const activeLimit = limit;

  return (
    <article className="col-lg-9 articleClass">
      <div className="article-body store-bd-gy">
        <div className="row align-items-center col-rever">
          <div className="col-sm-12 col-md-6 col-lg-6 mb-3">
            <div className="link-active">
              <p>
                <img src="assets/images/event-calender-selected.png" alt="" />{" "}
                {selectedGroup ? 'Communication Detail' : 'Communication History'} {total}
              </p>
            </div>
          </div>
          <div className="col-sm-12 col-md-6 col-lg-6 mb-3">
            <div className="d-flex align-items-center lg-justify-content-end">
              {selectedGroup ? (
                <button className="btn btn-outline-secondary" onClick={handleBackToGroups}>
                  Back to Groups
                </button>
              ) : (
                <form className="has-search">
                  <span className="form-search-icon form-control-feedback"></span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search Recipient"
                    value={search}
                    onChange={(e) => {
                      setActivePage(1);
                      setSearch(e.target.value);
                    }}
                  />
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="row mb-3">
          {companyOptions.length > 0 && (
            <div className="col-md-2 mb-2">
              <label className="form-label" style={{ fontSize: '0.85rem' }}>Company</label>
              <select
                className="form-control form-select"
                value={selectedCompany ? selectedCompany.value : ''}
                onChange={(e) => {
                  setActivePage(1);
                  const company = companyOptions.find(c => c.value === parseInt(e.target.value));
                  setSelectedCompany(company || null);
                }}
              >
                <option value="">All Companies</option>
                {companyOptions.map(company => (
                  <option key={company.value} value={company.value}>
                    {company.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="col-md-2 mb-2">
            <label className="form-label" style={{ fontSize: '0.85rem' }}>Type</label>
            <select
              className="form-control form-select"
              value={filterType}
              onChange={(e) => {
                setActivePage(1);
                setFilterType(e.target.value);
              }}
            >
              <option value="all">All Types</option>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
            </select>
          </div>

          {selectedGroup && (
            <div className="col-md-2 mb-2">
              <label className="form-label" style={{ fontSize: '0.85rem' }}>Status</label>
              <select
                className="form-control form-select"
                value={statusFilter}
                onChange={(e) => {
                  setActivePage(1);
                  setStatusFilter(e.target.value);
                }}
              >
                <option value="all">All Status</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          )}

          <div className="col-md-2 mb-2">
            <label className="form-label" style={{ fontSize: '0.85rem' }}>Start Date</label>
            <input
              type="date"
              className="form-control"
              value={startDate}
              onChange={(e) => {
                setActivePage(1);
                setStartDate(e.target.value);
              }}
            />
          </div>

          <div className="col-md-2 mb-2">
            <label className="form-label" style={{ fontSize: '0.85rem' }}>End Date</label>
            <input
              type="date"
              className="form-control"
              value={endDate}
              onChange={(e) => {
                setActivePage(1);
                setEndDate(e.target.value);
              }}
            />
          </div>

          <div className="col-md-2 mb-2">
            <label className="form-label" style={{ fontSize: '0.85rem' }}>Test Mode</label>
            <select
              className="form-control form-select"
              value={isTestFilter}
              onChange={(e) => {
                setActivePage(1);
                setIsTestFilter(e.target.value);
              }}
            >
              <option value="all">All</option>
              <option value="true">Test</option>
              <option value="false">Live</option>
            </select>
          </div>

          <div className="col-md-2 mb-2">
            <label className="form-label" style={{ fontSize: '0.85rem' }}>&nbsp;</label>
            <button
              className="btn btn-secondary w-100"
              style={{ marginTop: '7px' }}
              onClick={handleResetFilters}
            >
              Reset
            </button>
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-sm-12">
            <div className="table-responsive text-center">
              {error && (
                <div className="alert alert-danger mb-3">
                  {error}
                  <button type="button" className="btn-close float-end" onClick={() => setError(null)}></button>
                </div>
              )}

              <table className="table storetable">
                <thead className="text-center">
                  <tr>
                    {!selectedGroup && <th scope="col">Group ID</th>}
                    <th scope="col">Type</th>
                    {!selectedGroup && <th scope="col">Recipient Type</th>}
                    <th scope="col">Subject</th>
                    {selectedGroup && <th scope="col">Status</th>}
                    <th scope="col">Sent At</th>
                    {selectedGroup && <th scope="col">Recipient</th>}
                    {!selectedGroup && (
                      <>
                        <th scope="col">Total</th>
                        <th scope="col" className="text-success">Success</th>
                        <th scope="col" className="text-danger">Failed</th>
                      </>
                    )}
                    <th scope="col">Template</th>
                    {!selectedGroup && <th scope="col">Action</th>}
                  </tr>
                  <tr className="border-0 bg-none invisible">
                    <td className="border-0 bg-none"></td>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={selectedGroup ? 6 : 10}>
                        <div className="text-center py-5">
                          <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : history.length === 0 ? (
                    <tr>
                      <td colSpan={selectedGroup ? 6 : 10}>No data found.</td>
                    </tr>
                  ) : (
                    history.map((item) => (
                      <tr key={item.id}>
                        {!selectedGroup && <td>{item.id}</td>}
                        <td>{getTypeBadge(item.template_type)}</td>
                        {!selectedGroup && <td>{item.recipient_type || '-'}</td>}
                        <td>{(selectedGroup ? item.group?.subject : item.subject) || '-'}</td>
                        {selectedGroup && <td>{getStatusBadge(item.status)}</td>}
                        <td>{moment(item.sent_at).format('DD/MM/YYYY HH:mm')}</td>
                        {selectedGroup && <td>{item.recipient || '-'}</td>}
                        {!selectedGroup && (
                          <>
                            <td>{item.total_recipients || 0}</td>
                            <td className="text-success">{item.success_count || 0}</td>
                            <td className="text-danger">{item.failure_count || 0}</td>
                          </>
                        )}
                        <td>
                          <button
                            className="btn btn-link p-0 text-decoration-none"
                            onClick={() => handleOpenContentModal(
                              selectedGroup ? item.group?.subject : item.subject,
                              selectedGroup ? item.group?.content : item.content
                            )}
                            style={{ cursor: 'pointer' }}
                          >
                            {item.template?.name || 'View Content'}
                          </button>
                        </td>
                        {!selectedGroup && (
                          <td>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleViewDetails(item.id)}
                            >
                              Details
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {total > activeLimit && total && (
              <div className="d-flex justify-content-center mt-4">
                <nav aria-label="Page navigation">
                  <ul className="pagination">
                    <li className={`page-item ${activePage === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setActivePage(activePage - 1)}
                        disabled={activePage === 1}
                      >
                        Previous
                      </button>
                    </li>
                    {[...Array(Math.ceil(total / activeLimit))].map((_, index) => (
                      <li key={index} className={`page-item ${activePage === index + 1 ? 'active' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setActivePage(index + 1)}
                        >
                          {index + 1}
                        </button>
                      </li>
                    ))}
                    <li className={`page-item ${activePage === Math.ceil(total / activeLimit) ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setActivePage(activePage + 1)}
                        disabled={activePage === Math.ceil(total / activeLimit)}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </div>
      </div>

      {showContentModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{modalSubject}</h5>
                <button type="button" className="btn-close" onClick={handleCloseContentModal}></button>
              </div>
              <div className="modal-body">
                <div dangerouslySetInnerHTML={{ __html: modalContent }} />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseContentModal}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </article>
  );
};

export default CommunicationHistory;
